<template>
  <AiLoading v-if="isPending || !loaded" label="Gathering neighborhood rent data…" />

  <Text v-else-if="!hasData" variant="body-medium" color="tertiary">
    Not enough neighborhood data yet to show a price trend.
  </Text>

  <div v-else class="trend">
    <ul class="trend__legend">
      <li v-for="series in legend" :key="series.label" class="trend__legend-item">
        <span class="trend__swatch" :style="{ background: series.color }" />
        {{ series.label }}
      </li>
    </ul>

    <div class="chart">
      <Line :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  ScriptableContext,
  Title,
  Tooltip,
} from 'chart.js';
import { computed } from 'vue';
import { Line } from 'vue-chartjs';

import { AiLoading, Text } from '@tallo/design-system';
import { usePropertyPriceTrendQuery } from '@tallo/investor/property';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler);
// Match the chart text to the rest of the UI instead of Chart.js's Helvetica default.
ChartJS.defaults.font.family = 'Inter, sans-serif';

// Gate the loading state on `isPending` only (matches the other dashboard widgets): it's true just
// for the first load, when there's no data yet. Background refetches (e.g. on tab focus) leave the
// loaded chart on screen and swap in fresh data, instead of flashing the loading state over it.
const { data, isPending } = usePropertyPriceTrendQuery();
// `loaded` is false only while RentCast has not yet populated this property's data — we then keep
// showing the "gathering" state instead of the "no data" state. Defaults to true when the field is
// absent (e.g. a backend that predates this flag) so the widget never gets stuck gathering.
const loaded = computed(() => data.value?.loaded ?? true);

const points = computed(() => data.value?.points ?? []);
const hasData = computed(() => points.value.some((point) => point.averageRent != null));

const labels = computed(() => points.value.map((point) => useDateFormat(`${point.month}-01`, 'MMM YYYY').value));
const shortLabels = computed(() => points.value.map((point) => useDateFormat(`${point.month}-01`, 'MMM').value));

// Pull chart colors from the design tokens so the chart tracks the system, not hardcoded hexes.
const theme = computed(() => {
  const root = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const token = (name: string, fallback: string) => root?.getPropertyValue(name).trim() || fallback;
  return {
    average: token('--t-color-orange-500', '#f66b22'),
    fill: token('--t-color-orange-300', '#fbb176'),
    median: token('--t-color-yellow-300', '#ebaf47'),
    grid: token('--t-color-neutral-100', '#f1f2f2'),
    tick: token('--t-color-neutral-600', '#8b8e8f'),
    tooltipBg: token('--t-color-neutral-950', '#2b2b2c'),
    tooltipBody: token('--t-color-neutral-200', '#e4e6e6'),
    surface: token('--t-color-neutral-white', '#ffffff'),
  };
});

const hasMedian = computed(() => points.value.some((point) => point.medianRent != null));
const legend = computed(() => [
  { label: 'Average rent', color: theme.value.average },
  ...(hasMedian.value ? [{ label: 'Median rent', color: theme.value.median }] : []),
]);

const chartData = computed<ChartData<'line'>>(() => ({
  labels: labels.value,
  datasets: [
    {
      label: 'Average rent',
      data: points.value.map((point) => point.averageRent),
      borderColor: theme.value.average,
      backgroundColor: (context: ScriptableContext<'line'>) => areaFill(context, theme.value.fill),
      pointBackgroundColor: theme.value.average,
      pointHoverBackgroundColor: theme.value.average,
      pointHoverBorderColor: theme.value.surface,
      pointHoverBorderWidth: 2,
      tension: 0.4,
      fill: true,
      borderWidth: 2,
    },
    {
      label: 'Median rent',
      data: points.value.map((point) => point.medianRent),
      borderColor: theme.value.median,
      backgroundColor: 'transparent',
      pointBackgroundColor: theme.value.median,
      pointHoverBackgroundColor: theme.value.median,
      pointHoverBorderColor: theme.value.surface,
      pointHoverBorderWidth: 2,
      tension: 0.4,
      fill: false,
      borderWidth: 2,
    },
  ],
}));

const formatRent = (value: number | string) => `$${Number(value).toLocaleString('en-US')}`;

// Warm wash under the average line that fades toward the baseline without vanishing (Figma).
function areaFill(context: ScriptableContext<'line'>, color: string): CanvasGradient | string {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) return 'transparent';
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, hexToRgba(color, 0.22));
  gradient.addColorStop(1, hexToRgba(color, 0.04));
  return gradient;
}

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized;
  const int = parseInt(full, 16);
  if (Number.isNaN(int)) return `rgba(246, 107, 34, ${alpha})`;
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
}

const chartOptions = computed<ChartOptions<'line'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false, axis: 'x' },
  layout: { padding: { top: 4 } },
  plugins: {
    // The legend is HTML above the chart, as in Figma
    legend: { display: false },
    tooltip: {
      backgroundColor: theme.value.tooltipBg,
      titleColor: theme.value.surface,
      bodyColor: theme.value.tooltipBody,
      padding: 12,
      cornerRadius: 12,
      boxPadding: 6,
      usePointStyle: true,
      titleFont: { size: 12, weight: 600 },
      bodyFont: { size: 12, weight: 400 },
      callbacks: {
        label: (context) => `${context.dataset.label}: ${formatRent(context.parsed.y)}`,
      },
    },
  },
  elements: { point: { radius: 0, hoverRadius: 5 } },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: {
        color: theme.value.tick,
        font: { size: 12 },
        maxRotation: 0,
        autoSkip: false,
        // The end labels sit inside the plot, so the lines run edge to edge
        align: 'inner',
        padding: 10,
        // Every other month, as in Figma
        callback: (_value, index) => (index % 2 === 0 ? (shortLabels.value[index] ?? '') : ''),
      },
    },
    y: {
      grace: '12%',
      grid: { color: theme.value.grid, drawTicks: false },
      border: { display: false },
      // No rent labels on the axis (Figma): the tooltip gives each month's rents
      ticks: { display: false, maxTicksLimit: 6 },
    },
  },
}));
</script>

<style scoped lang="scss">
.trend {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-md);
}

.trend__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem var(--t-spacing-md);
  margin: 0;
  padding: 0;
  list-style: none;
}

.trend__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font: var(--t-typography-body-sm-font);
  color: var(--t-text-color-tertiary);
}

.trend__swatch {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 2px;
  flex-shrink: 0;
}

.chart {
  height: 15rem;
  width: 100%;
}
</style>
